// primitive buttons are used here for native level screen reader support
/* eslint-disable @atlaskit/design-system/no-html-button */
import React, { type KeyboardEvent } from 'react';
import {
	SCOPE_GLOBAL,
	SCOPE_PROJECT,
} from '@jira/project-settings__workflow-types/src/workflowConstants.tsx';
import type { ScopeType } from '@jira/project-settings__workflow-types/src/workflowTypes.types.tsx';
import { Text } from '@atlaskit/primitives/compiled';
import { fg } from '@atlassian/jira-feature-gating';
import { defineMessage, useIntl } from '@atlassian/jira-intl';
import { DIRECTION_KEYS, NODE_TYPE } from './directionKeys.tsx';
import type { Positionable, ListNodeDetails, NodeId, ItemReference } from './ListEdgeDetails.tsx';
import ValidationIndicator from './ValidationIndicator.tsx';

export const STANDARD_MOVE_DISTANCE = 5;
export const GREATER_MOVE_DISTANCE = 10;
export const FINE_MOVE_DISTANCE = 1;

const NodeLabel = ({
	node,
	workflowScope,
}: {
	node: ListNodeDetails;
	/**
	 * Make this a required prop when clean up sprt_hix_9246_wfe_diagram_complete_workflowscope
	 */
	workflowScope?: ScopeType;
}) => {
	const { formatMessage } = useIntl();

	if (node.status) {
		if (!node.status.name) {
			return <Text>{formatMessage(unknownStatusNodeLabel)}</Text>;
		}

		const shouldAnnounceGlobalStatus =
			workflowScope === SCOPE_PROJECT && node.status.scope === SCOPE_GLOBAL;
		if (shouldAnnounceGlobalStatus) {
			return <Text>{formatMessage(globalStatusNodeLabel, { statusName: node.status.name })}</Text>;
		}

		return <Text>{formatMessage(statusNodeLabel, { statusName: node.status.name })}</Text>;
	}

	return (
		<Text>
			{formatMessage(
				fg('jira-issue-terminology-refresh-m3') ? startNodeLabelIssueTermRefresh : startNodeLabel,
			)}
		</Text>
	);
};

export type Props = {
	node: ListNodeDetails;
	selectedItem?: ItemReference;
	isImmutable?: boolean;
	describedBy?: string;
	/**
	 * Make this a required prop when clean up sprt_hix_9246_wfe_diagram_complete_workflowscope
	 */
	workflowScope?: ScopeType;
	onFocus?: (id: NodeId) => void;
	onSelect?: (item: ItemReference | undefined) => void;
	onMoveStart?: (id: Positionable) => void;
	onMoveUpdate?: (id: Positionable) => void;
	onMoveComplete?: () => void;
	onMoveCancel?: () => void;
};

const ListNode = ({
	node,
	selectedItem,
	isImmutable = false,
	describedBy,
	workflowScope,
	onSelect,
	onFocus,
	onMoveStart,
	onMoveUpdate,
	onMoveComplete,
	onMoveCancel,
}: Props): React.JSX.Element => {
	const isSelected =
		!!selectedItem && selectedItem.type === NODE_TYPE && selectedItem.id === node.id;

	const handleClick = () => {
		if (!node.isMoving && !node.isInitial) {
			onSelect?.({ type: 'node', id: node.id });
		}
	};

	const handleFocus = () => {
		onFocus?.(node.id);
	};

	const handleKeyDown = (e: KeyboardEvent) => {
		const direction = DIRECTION_KEYS[e.key];

		if (node.isMoving && direction && node.position) {
			const updatedPosition = { ...node.position };

			let moveDistance = STANDARD_MOVE_DISTANCE;

			if (e.shiftKey) {
				moveDistance = GREATER_MOVE_DISTANCE;
			} else if (e.altKey) {
				moveDistance = FINE_MOVE_DISTANCE;
			}

			if (direction === 'up') {
				updatedPosition.y -= moveDistance;
			}
			if (direction === 'down') {
				updatedPosition.y += moveDistance;
			}
			if (direction === 'left') {
				updatedPosition.x -= moveDistance;
			}
			if (direction === 'right') {
				updatedPosition.x += moveDistance;
			}

			onMoveUpdate?.({
				id: node.id,
				type: node.isInitial ? 'start-node' : 'status',
				position: updatedPosition,
			});
		}
	};

	const handleKeyUp = (e: KeyboardEvent) => {
		if (e.key === 'm' && !node.isMoving && node.position) {
			onMoveStart?.({
				id: node.id,
				type: node.isInitial ? 'start-node' : 'status',
				position: node.position,
			});
		}

		if ((e.key === 'Enter' || e.key === ' ') && node.isMoving) {
			onMoveComplete?.();
		}

		if (e.key === 'Escape' && node.isMoving) {
			onMoveCancel?.();
		}
	};

	const content = (
		<>
			<NodeLabel node={node} workflowScope={workflowScope} />{' '}
			<ValidationIndicator validationFlag={node.validationFlag} />
		</>
	);

	if (isImmutable) {
		return <div aria-current={isSelected}>{content}</div>;
	}

	return (
		<button
			type="button"
			aria-current={isSelected}
			onClick={handleClick}
			onFocus={handleFocus}
			onKeyDown={handleKeyDown}
			onKeyUp={handleKeyUp}
			aria-describedby={describedBy}
		>
			{content}
		</button>
	);
};

export default ListNode;

const startNodeLabel = defineMessage({
	id: 'accessible-workflow-diagram.list-navigation.list-node.start-node-label',
	defaultMessage: "Start - work items transition from here when they're created.",
	description: 'Label for start node',
});

const startNodeLabelIssueTermRefresh = defineMessage({
	id: 'accessible-workflow-diagram.list-navigation.list-node.start-node-label-issue-term-refresh',
	defaultMessage: 'Start - transition from here once created.',
	description: 'Label for start node',
});

const statusNodeLabel = defineMessage({
	id: 'accessible-workflow-diagram.list-navigation.list-node.status-node-label',
	defaultMessage: '{statusName} status.',
	description: 'Label for a status node',
});

const globalStatusNodeLabel = defineMessage({
	id: 'accessible-workflow-diagram.list-navigation.list-node.global-status-node-label',
	defaultMessage: '{statusName} status. Global status.',
	description: 'Label for a global status node in team-managed space',
});

const unknownStatusNodeLabel = defineMessage({
	id: 'accessible-workflow-diagram.list-navigation.list-node.unknown-status-node-label',
	defaultMessage: 'Unknown status.',
	description: 'Label for a unknown status node',
});
